import {
  initPostHog,
  identifyUser as posthogIdentify,
  captureEvent as posthogCapture,
  registerSuperProperties,
  setGroup,
  resetPostHog,
} from "./posthog";

const eventName = ({ name, formName, action, status }) =>
  name || [formName, action, status].filter(Boolean).join(".") || "event";

// Clarity is injected by the boot script in public/index.html (only on tenants
// with head.clarityId, never on /embed or non-prod hosts), so window.clarity is
// absent elsewhere. Only an internal user id and tenant id are sent - no PII.
const clarity = (...args) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity(...args);
    }
  } catch {
    return;
  }
};

const registerTenantContext = () => {
  const tenant = (typeof window !== "undefined" && window.__TENANT__) || {};
  const tenantId = tenant.id || "default";
  const tenantName = (tenant.head && tenant.head.title) || tenant.id || "default";
  registerSuperProperties({ tenant_id: tenantId, tenant_name: tenantName });
  setGroup("tenant", tenantId, { name: tenantName });
  clarity("set", "tenant_id", tenantId);
};

const DEEP_LINK_PATTERNS = [
  /\/want-(ad|listing)/i,
  /\/inventory/i,
  /\/property/i,
  /\/introduction/i,
  /\/share/i,
];

let iframeListenerAttached = false;

const safeReferrer = () => {
  try {
    if (!document.referrer) return undefined;
    const { origin, pathname } = new URL(document.referrer);
    return `${origin}${pathname}`;
  } catch {
    return undefined;
  }
};

const trackDeepLinkEntry = () => {
  try {
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search || "");
    const hasCampaign =
      params.has("ref") ||
      params.has("invite") ||
      params.has("utm_source") ||
      params.has("utm_campaign");
    if (!hasCampaign && !DEEP_LINK_PATTERNS.some((re) => re.test(pathname))) {
      return;
    }
    posthogCapture("deep_link_opened", {
      target_path: pathname,
      ref: params.get("ref") || undefined,
      utm_source: params.get("utm_source") || undefined,
      utm_campaign: params.get("utm_campaign") || undefined,
      referrer: safeReferrer(),
    });
  } catch {
    return;
  }
};

const trackIframeClicks = () => {
  if (iframeListenerAttached) return;
  try {
    iframeListenerAttached = true;
    let lastFired = 0;
    window.addEventListener("blur", () => {
      try {
        const el = document.activeElement;
        if (!el || el.tagName !== "IFRAME") return;
        const now = Date.now();
        if (now - lastFired < 1000) return;
        lastFired = now;
        let host;
        try {
          host = el.src ? new URL(el.src).host : undefined;
        } catch {
          host = undefined;
        }
        posthogCapture("iframe_clicked", {
          iframe_id: el.id || undefined,
          iframe_title: el.title || undefined,
          iframe_host: host,
          page_path: window.location.pathname,
        });
      } catch {
        return;
      }
    });
  } catch {
    return;
  }
};

export const initAnalytics = () => {
  try {
    initPostHog();
    registerTenantContext();
    if (typeof window !== "undefined") {
      window.__analytics = { trackEvent, identify, reset, group };
      trackDeepLinkEntry();
      trackIframeClicks();
    }
  } catch {
    return;
  }
};

export const trackEvent = ({
  name,
  formName,
  action,
  status,
  attributes = {},
  errorMessage,
} = {}) => {
  const resolvedName = eventName({ name, formName, action, status });
  const properties = {
    ...attributes,
    ...(formName ? { formName } : {}),
    ...(action ? { action } : {}),
    ...(status ? { status } : {}),
    ...(errorMessage ? { errorMessage } : {}),
  };

  posthogCapture(resolvedName, properties);
};

export const identify = (userId, properties = {}) => {
  posthogIdentify(userId, properties);
  if (userId !== undefined && userId !== null && userId !== "") {
    clarity("identify", String(userId));
  }
};

export const group = (groupType, groupKey, properties = {}) => {
  setGroup(groupType, groupKey, properties);
};

// Clarity has no reset/logout call: after a sign-out its session stays tied to
// the last identify()'d id until it expires, so only PostHog is reset here.
export const reset = () => {
  resetPostHog();
};
